import { useMemo, type MouseEvent as ReactMouseEvent } from 'react'
import {
  ArrowUpOutlined,
  FileOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  HomeOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { LocalFileEntry } from './types'
import type { FtpSortConfig, FtpSortField } from './types'
import { filterAndSortItems, formatDate, formatFileSize, isDirectory, normalizePath, parentPath } from './utils'
import { getLocalChildren } from './localBrowser'
import styles from './FtpClient.module.css'

interface FtpLocalPanelProps {
  rootLabel: string
  entries: LocalFileEntry[]
  currentPath: string
  pathInput: string
  selected: Set<string>
  searchQuery: string
  sort: FtpSortConfig
  onPathInputChange: (v: string) => void
  onNavigate: (path: string) => void
  onSelect: (item: LocalFileEntry, e?: React.MouseEvent) => void
  onOpen: (item: LocalFileEntry) => void
  onPickFolder: () => void
  onAddFiles: () => void
  onDropFiles: (files: FileList) => void
  onToggleSort: (field: FtpSortField) => void
}

export function FtpLocalPanel({
  rootLabel,
  entries,
  currentPath,
  pathInput,
  selected,
  searchQuery,
  sort,
  onPathInputChange,
  onNavigate,
  onSelect,
  onOpen,
  onPickFolder,
  onAddFiles,
  onDropFiles,
  onToggleSort,
}: FtpLocalPanelProps) {
  const children = useMemo(() => getLocalChildren(entries, currentPath), [entries, currentPath])

  const asFtpItems = useMemo(
    () =>
      children.map((c) => ({
        name: c.name,
        type: c.type === 'directory' ? ('directory' as const) : ('file' as const),
        size: c.size,
        modifyTime: c.modifyTime ? new Date(c.modifyTime).toISOString() : null,
        permissions: '',
        path: c.path,
      })),
    [children],
  )

  const filtered = useMemo(
    () => filterAndSortItems(asFtpItems, searchQuery, sort),
    [asFtpItems, searchQuery, sort],
  )

  const toggleSort = (field: FtpSortField) => onToggleSort(field)

  const sortMark = (field: FtpSortField) =>
    sort.field === field ? (sort.direction === 'asc' ? '▲' : '▼') : '⇅'

  const hasEntries = entries.length > 0

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Локальный компьютер</span>
        <div className={styles.panelActions}>
          <button type="button" className={styles.toolBtn} title="Открыть папку" onClick={onPickFolder}>
            <FolderOpenOutlined />
          </button>
          <button type="button" className={styles.toolBtn} title="Добавить файлы" onClick={onAddFiles}>
            <FileOutlined />
          </button>
        </div>
      </div>

      <div className={styles.pathBar}>
        <button type="button" className={styles.toolBtn} title="Корень" onClick={() => onNavigate('/')}>
          <HomeOutlined />
        </button>
        <button
          type="button"
          className={styles.toolBtn}
          title="Вверх"
          disabled={currentPath === '/'}
          onClick={() => onNavigate(parentPath(currentPath))}
        >
          <ArrowUpOutlined />
        </button>
        <input
          className={styles.pathInput}
          value={pathInput}
          onChange={(e) => onPathInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onNavigate(normalizePath(pathInput))
          }}
        />
        <button type="button" className={styles.toolBtn} title="Обновить" onClick={() => onNavigate(currentPath)}>
          <ReloadOutlined />
        </button>
      </div>

      <div
        className={styles.viewContainer}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault()
          if (e.dataTransfer.files?.length) onDropFiles(e.dataTransfer.files)
        }}
      >
        {!hasEntries ? (
          <div className={styles.emptyState}>
            <FolderOpenOutlined style={{ fontSize: 28, color: 'var(--text-tertiary)' }} />
            <p className={styles.emptyTitle}>Локальная папка не выбрана</p>
            <p className={styles.emptyHint}>Откройте папку или перетащите файлы сюда для загрузки на сервер</p>
            <button type="button" className={styles.btnPrimary} onClick={onPickFolder}>
              Выбрать папку
            </button>
          </div>
        ) : (
          <table className={styles.fileTable}>
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')}>
                  Имя <span className={sort.field === 'name' ? styles.sortIconActive : styles.sortIcon}>{sortMark('name')}</span>
                </th>
                <th onClick={() => toggleSort('size')}>
                  Размер <span className={sort.field === 'size' ? styles.sortIconActive : styles.sortIcon}>{sortMark('size')}</span>
                </th>
                <th onClick={() => toggleSort('date')}>
                  Дата <span className={sort.field === 'date' ? styles.sortIconActive : styles.sortIcon}>{sortMark('date')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const local = children.find((c) => c.path === item.path)
                if (!local) return null
                return (
                  <tr
                    key={item.path}
                    className={selected.has(item.path) ? styles.rowSelected : undefined}
                    onClick={(e) => onSelect(local, e)}
                    onDoubleClick={() => onOpen(local)}
                  >
                    <td>
                      <div className={styles.nameCell}>
                        <span className={styles.fileIcon}>
                          {isDirectory(item) ? <FolderOutlined /> : <FileOutlined />}
                        </span>
                        {item.name}
                      </div>
                    </td>
                    <td>{isDirectory(item) ? '—' : formatFileSize(item.size)}</td>
                    <td>{item.modifyTime ? formatDate(item.modifyTime) : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.panelFooter}>
        <span>{rootLabel}</span>
        <span>{filtered.length} элементов · {selected.size} выбрано</span>
      </div>
    </div>
  )
}
